import React, { useState } from 'react';
import { Button } from '../components/Button';
import { useScrollAnimation } from '../hooks/useScrollAnimation';
import { CheckIcon, XIcon, StarIcon, QuoteIcon, PhoneForwardedIcon, MessageCircleIcon, CalendarCheckIcon, ZapIcon, WorkflowIcon, DatabaseIcon, BrainCircuitIcon, CheckCircleIcon, ShieldCheckIcon, ClockIcon, AwardIcon, PhoneIcon, BellIcon, ClipboardCheckIcon, SendIcon, ChevronDownIcon } from 'lucide-react';
function ProfessionalHero() {
  return <section className="w-full bg-[#0F0F0F] py-20 md:py-32" data-id="element-2305">
      <div className="max-w-7xl mx-auto px-8 md:px-12" data-id="element-2306">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center" data-id="element-2307">
          <div data-id="element-2308">
            <div className="inline-block bg-[#F1B530] text-[#0F0F0F] px-4 py-2 rounded-full font-['Inter'] font-bold text-sm mb-6 animate-[fadeInUp_0.8s_ease-out]" data-id="element-2309">
              TRUSTED BY 1000+ BUSINESSES
            </div>

            <h1 className="font-['Poppins'] font-bold text-5xl md:text-6xl lg:text-7xl text-[#F5F5F5] mb-6 leading-tight animate-[fadeInUp_0.8s_ease-out_0.2s] opacity-0 [animation-fill-mode:forwards]" data-id="element-2310">
              Professional AI Call Answering Service
            </h1>

            <p className="font-['Inter'] text-xl text-[#A0A0A0] mb-8 leading-relaxed font-light animate-[fadeInUp_0.8s_ease-out_0.4s] opacity-0 [animation-fill-mode:forwards]" data-id="element-2311">
              Never miss an important call again. Our AI receptionist answers
              24/7, captures every detail, and integrates seamlessly with your
              business operations.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 mb-8 animate-[fadeInUp_0.8s_ease-out_0.6s] opacity-0 [animation-fill-mode:forwards]" data-id="element-2312">
              <Button size="lg" data-id="element-2313">Get Started Free</Button>
              <Button variant="secondary" size="lg" theme="dark" data-id="element-2314">
                Request Demo
              </Button>
            </div>

            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:gap-6 animate-[fadeInUp_0.8s_ease-out_0.8s] opacity-0 [animation-fill-mode:forwards]" data-id="element-2315">
              <div className="flex items-center gap-2 text-sm text-[#A0A0A0] font-['Inter']" data-id="element-2316">
                <CheckIcon className="w-5 h-5 text-[#F1B530]" data-id="element-2317" />
                <span data-id="element-2318">No credit card required</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-[#A0A0A0] font-['Inter']" data-id="element-2319">
                <CheckIcon className="w-5 h-5 text-[#F1B530]" data-id="element-2320" />
                <span data-id="element-2321">7-day free trial</span>
              </div>
              <div className="flex items-center gap-2" data-id="element-2322">
                {[...Array(5)].map((_, i) => <StarIcon key={i} className="w-5 h-5 text-[#F1B530] fill-current" data-id="element-2323" />)}
                <span className="font-['Inter'] text-sm text-[#A0A0A0] ml-1" data-id="element-2324">
                  4.9/5 from 500+ reviews
                </span>
              </div>
            </div>
          </div>

          <div className="relative animate-[fadeInUp_0.8s_ease-out_1s] opacity-0 [animation-fill-mode:forwards]" data-id="element-2325">
            <div className="bg-[#252525] border border-[#2A2A2A] rounded-3xl p-8 shadow-2xl" data-id="element-2326">
              <img src="https://images.unsplash.com/photo-1556761175-b413da4baf72?w=800&q=80" alt="Professional service" className="w-full h-96 object-cover rounded-2xl" data-id="element-2327" />
            </div>

            {/* Floating Stats */}
            <div className="absolute -bottom-6 -left-6 bg-[#252525]/90 backdrop-blur-md border border-[#F1B530]/30 rounded-2xl p-6 shadow-xl" data-id="element-2328">
              <div className="font-['Poppins'] text-4xl font-bold text-[#F1B530] mb-1" data-id="element-2329">
                99.9%
              </div>
              <div className="font-['Inter'] text-sm text-[#A0A0A0]" data-id="element-2330">
                Call Answer Rate
              </div>
            </div>
          </div>
        </div>
      </div>

      <style data-id="element-2331">{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </section>;
}
function TrustBadgesAnimated() {
  const {
    ref,
    isVisible
  } = useScrollAnimation({
    threshold: 0.1
  });
  const badges = [{
    icon: CheckCircleIcon,
    label: '99.9% Uptime',
    value: 'Guaranteed'
  }, {
    icon: ShieldCheckIcon,
    label: 'Enterprise Security',
    value: 'SOC 2 Certified'
  }, {
    icon: ClockIcon,
    label: '24/7 Support',
    value: 'Always Available'
  }, {
    icon: AwardIcon,
    label: '10,000+ Calls',
    value: 'Handled Daily'
  }];
  return <section className="w-full bg-[#1A1A1A] py-16" data-id="element-2332">
      <div ref={ref} className={`max-w-7xl mx-auto px-8 md:px-12 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`} data-id="element-2333">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8" data-id="element-2334">
          {badges.map((badge, index) => {
          const Icon = badge.icon;
          return <div key={index} className="text-center" data-id="element-2335">
                <div className="w-16 h-16 bg-[#F1B530]/10 rounded-2xl flex items-center justify-center mx-auto mb-4 border border-[#F1B530]/20" data-id="element-2336">
                  <Icon className="w-8 h-8 text-[#F1B530]" data-id="element-2337" />
                </div>
                <div className="font-['Poppins'] text-2xl font-bold text-[#F5F5F5] mb-1" data-id="element-2338">
                  {badge.label}
                </div>
                <div className="font-['Inter'] text-sm text-[#A0A0A0]" data-id="element-2339">
                  {badge.value}
                </div>
              </div>;
        })}
        </div>
      </div>
    </section>;
}
function ServiceBreakdown() {
  const {
    ref,
    isVisible
  } = useScrollAnimation({
    threshold: 0.1
  });
  const services = [{
    title: 'Call Answering',
    description: 'Professional greeting and message capture for every caller',
    features: ['Custom greetings', 'Message transcription', 'Real-time notifications']
  }, {
    title: 'Appointment Booking',
    description: 'Automated scheduling integrated with your calendar',
    features: ['Calendar sync', 'Booking confirmations', 'Reminder notifications']
  }, {
    title: 'Lead Qualification',
    description: 'Intelligent screening and routing of potential customers',
    features: ['Custom questions', 'Lead scoring', 'CRM integration']
  }, {
    title: 'After Hours Support',
    description: '24/7 coverage when your team is unavailable',
    features: ['Always available', 'Urgent call routing', 'Emergency protocols']
  }];
  return <section className="w-full bg-[#1A1A1A] py-28" data-id="element-2340">
      <div ref={ref} className={`max-w-7xl mx-auto px-8 md:px-12 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`} data-id="element-2341">
        <div className="text-center mb-16" data-id="element-2342">
          <h2 className="font-['Poppins'] font-bold text-5xl md:text-6xl text-[#F5F5F5] mb-6" data-id="element-2343">
            Complete Call Management
          </h2>
          <p className="font-['Inter'] text-xl text-[#A0A0A0] font-light max-w-3xl mx-auto" data-id="element-2344">
            Everything you need to handle calls professionally and efficiently
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-2345">
          {services.map((service, index) => <div key={index} className="bg-[#252525] border border-[#2A2A2A] rounded-2xl p-8 hover:border-[#F1B530]/30 transition-all duration-300" data-id="element-2346">
              <h3 className="font-['Poppins'] font-bold text-2xl text-[#F5F5F5] mb-4" data-id="element-2347">
                {service.title}
              </h3>
              <p className="font-['Inter'] text-[#A0A0A0] mb-6 leading-relaxed font-light" data-id="element-2348">
                {service.description}
              </p>
              <ul className="space-y-3" data-id="element-2349">
                {service.features.map((feature, idx) => <li key={idx} className="flex items-center gap-3" data-id="element-2350">
                    <CheckIcon className="w-5 h-5 text-[#F1B530] flex-shrink-0" data-id="element-2351" />
                    <span className="font-['Inter'] text-[#F5F5F5] text-sm" data-id="element-2352">
                      {feature}
                    </span>
                  </li>)}
              </ul>
            </div>)}
        </div>
      </div>
    </section>;
}
function ComparisonTable() {
  const {
    ref,
    isVisible
  } = useScrollAnimation({
    threshold: 0.1
  });
  const features = ['Instant call pickup', 'Custom greeting', 'Message capture', 'Call recordings', 'Real-time notifications', 'Appointment booking', 'CRM integration', 'After hours coverage', 'Multi-language support'];
  return <section className="w-full bg-[#0F0F0F] py-28" data-id="element-2353">
      <div ref={ref} className={`max-w-5xl mx-auto px-8 md:px-12 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`} data-id="element-2354">
        <div className="text-center mb-16" data-id="element-2355">
          <h2 className="font-['Poppins'] font-bold text-5xl md:text-6xl text-[#F5F5F5] mb-6" data-id="element-2356">
            Why Choose AI Over Traditional Services?
          </h2>
        </div>

        <div className="bg-[#252525] border border-[#2A2A2A] rounded-3xl overflow-hidden" data-id="element-2357">
          <div className="grid grid-cols-3 gap-px bg-[#2A2A2A]" data-id="element-2358">
            <div className="bg-[#252525] p-6" data-id="element-2359">
              <div className="font-['Inter'] font-semibold text-[#A0A0A0] text-sm" data-id="element-2360">
                FEATURE
              </div>
            </div>
            <div className="bg-[#252525] p-6 text-center" data-id="element-2361">
              <div className="font-['Inter'] font-bold text-[#F1B530] text-lg" data-id="element-2362">
                AI Service
              </div>
            </div>
            <div className="bg-[#252525] p-6 text-center" data-id="element-2363">
              <div className="font-['Inter'] font-semibold text-[#A0A0A0] text-sm" data-id="element-2364">
                Traditional
              </div>
            </div>
          </div>

          {features.map((feature, index) => <div key={index} className="grid grid-cols-3 gap-px bg-[#2A2A2A]" data-id="element-2365">
              <div className="bg-[#252525] p-6" data-id="element-2366">
                <div className="font-['Inter'] text-[#F5F5F5]" data-id="element-2367">{feature}</div>
              </div>
              <div className="bg-[#252525] p-6 flex justify-center" data-id="element-2368">
                <CheckIcon className="w-6 h-6 text-[#F1B530]" data-id="element-2369" />
              </div>
              <div className="bg-[#252525] p-6 flex justify-center" data-id="element-2370">
                {index < 6 ? <CheckIcon className="w-6 h-6 text-[#A0A0A0]" data-id="element-2371" /> : <XIcon className="w-6 h-6 text-[#2A2A2A]" data-id="element-2372" />}
              </div>
            </div>)}
        </div>
      </div>
    </section>;
}
function OfficeHQPricing() {
  const {
    ref,
    isVisible
  } = useScrollAnimation({
    threshold: 0.1
  });
  const plans = [{
    name: 'Starter',
    price: 'AUD $99',
    period: '/month',
    description: 'Perfect for small businesses',
    features: ['50 calls per month', 'Basic call answering', 'Message capture', 'Email notifications', 'Standard support'],
    cta: 'Start Free Trial'
  }, {
    name: 'Professional',
    price: 'AUD $249',
    period: '/month',
    description: 'Most popular for growing teams',
    features: ['200 calls per month', 'Advanced call routing', 'Appointment booking', 'CRM integration', 'Priority support', 'Call analytics'],
    cta: 'Start Free Trial',
    isPopular: true
  }, {
    name: 'Enterprise',
    price: 'Custom',
    period: '',
    description: 'For large organizations',
    features: ['Unlimited calls', 'Dedicated account manager', 'Custom integrations', 'Advanced analytics', '24/7 premium support', 'SLA guarantee'],
    cta: 'Contact Sales'
  }];
  return <section className="w-full bg-[#1A1A1A] py-28" data-id="element-2373">
      <div ref={ref} className={`max-w-7xl mx-auto px-8 md:px-12 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`} data-id="element-2374">
        <div className="text-center mb-16" data-id="element-2375">
          <h2 className="font-['Poppins'] font-bold text-5xl md:text-6xl text-[#F5F5F5] mb-6" data-id="element-2376">
            Simple, Transparent Pricing
          </h2>
          <p className="font-['Inter'] text-xl text-[#A0A0A0] font-light" data-id="element-2377">
            Choose the plan that fits your business needs
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-2378">
          {plans.map((plan, index) => <div key={index} className={`bg-[#252525] rounded-2xl p-8 border transition-all duration-300 hover:-translate-y-1 relative ${plan.isPopular ? 'border-[#F1B530] ring-2 ring-[#F1B530]/20' : 'border-[#2A2A2A] hover:border-[#F1B530]/30'}`} data-id="element-2379">
              {plan.isPopular && <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 bg-[#F1B530] text-[#0F0F0F] px-4 py-1 rounded-full font-['Inter'] font-semibold text-sm" data-id="element-2380">
                  Most Popular
                </div>}

              <div className="mb-6" data-id="element-2381">
                <h3 className="font-['Poppins'] font-bold text-2xl text-[#F5F5F5] mb-2" data-id="element-2382">
                  {plan.name}
                </h3>
                <p className="font-['Inter'] text-sm text-[#A0A0A0] font-light" data-id="element-2383">
                  {plan.description}
                </p>
              </div>

              <div className="mb-8" data-id="element-2384">
                <span className="font-['Poppins'] font-bold text-5xl text-[#F5F5F5]" data-id="element-2385">
                  {plan.price}
                </span>
                <span className="font-['Inter'] text-[#A0A0A0] ml-1" data-id="element-2386">
                  {plan.period}
                </span>
              </div>

              <ul className="space-y-4 mb-8" data-id="element-2387">
                {plan.features.map((feature, idx) => <li key={idx} className="flex items-start gap-3" data-id="element-2388">
                    <CheckIcon className="w-5 h-5 text-[#F1B530] flex-shrink-0 mt-0.5" data-id="element-2389" />
                    <span className="font-['Inter'] text-[#F5F5F5] text-sm font-light" data-id="element-2390">
                      {feature}
                    </span>
                  </li>)}
              </ul>

              <Button className="w-full" size="lg" data-id="element-2391">
                {plan.cta}
              </Button>
            </div>)}
        </div>
      </div>
    </section>;
}
function OfficeHQTestimonials() {
  const {
    ref,
    isVisible
  } = useScrollAnimation({
    threshold: 0.1
  });
  const testimonials = [{
    quote: 'The AI answering service has been a game-changer for our law firm. We never miss a potential client call, even after hours.',
    author: 'Michael Chen',
    role: 'Managing Partner, Chen & Associates',
    image: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=400&q=80',
    rating: 5
  }, {
    quote: 'Professional, reliable, and cost-effective. Our clients love the immediate response, and we love the detailed call summaries.',
    author: 'Sarah Johnson',
    role: 'Operations Director, MedCare Clinic',
    image: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=400&q=80',
    rating: 5
  }, {
    quote: 'Setup was incredibly easy, and the integration with our CRM was seamless. Best investment we made this year.',
    author: 'David Martinez',
    role: 'CEO, RealtyPro Group',
    image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400&q=80',
    rating: 5
  }];
  return <section className="w-full bg-[#0F0F0F] py-28" data-id="element-2392">
      <div ref={ref} className={`max-w-7xl mx-auto px-8 md:px-12 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`} data-id="element-2393">
        <div className="text-center mb-16" data-id="element-2394">
          <h2 className="font-['Poppins'] font-bold text-5xl md:text-6xl text-[#F5F5F5] mb-6" data-id="element-2395">
            Trusted by Professionals
          </h2>
          <p className="font-['Inter'] text-xl text-[#A0A0A0] font-light" data-id="element-2396">
            See what our clients have to say
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-2397">
          {testimonials.map((testimonial, index) => <div key={index} className="bg-[#252525] border border-[#2A2A2A] rounded-2xl p-8 hover:border-[#F1B530]/30 transition-all duration-300" data-id="element-2398">
              <div className="flex gap-1 mb-6" data-id="element-2399">
                {[...Array(testimonial.rating)].map((_, i) => <StarIcon key={i} className="w-5 h-5 text-[#F1B530] fill-current" data-id="element-2400" />)}
              </div>

              <QuoteIcon className="w-10 h-10 text-[#F1B530]/20 mb-4" data-id="element-2401" />

              <p className="font-['Inter'] text-[#F5F5F5] mb-8 leading-relaxed font-light" data-id="element-2402">
                "{testimonial.quote}"
              </p>

              <div className="flex items-center gap-4" data-id="element-2403">
                <img src={testimonial.image} alt={testimonial.author} className="w-12 h-12 rounded-full object-cover border-2 border-[#F1B530]/20" data-id="element-2404" />
                <div data-id="element-2405">
                  <div className="font-['Inter'] font-semibold text-[#F5F5F5]" data-id="element-2406">
                    {testimonial.author}
                  </div>
                  <div className="font-['Inter'] text-sm text-[#A0A0A0]" data-id="element-2407">
                    {testimonial.role}
                  </div>
                </div>
              </div>
            </div>)}
        </div>
      </div>
    </section>;
}
function OfficeHQAddOns() {
  const {
    ref,
    isVisible
  } = useScrollAnimation({
    threshold: 0.1
  });
  const addOns = [{
    icon: PhoneForwardedIcon,
    name: 'Live Transfer',
    price: 'AUD $99',
    setup: 'one-time setup',
    features: ['Instant call escalation', 'Full caller context transfer', 'Smart routing rules']
  }, {
    icon: MessageCircleIcon,
    name: 'FAQ Handling',
    price: 'AUD $299',
    setup: 'one-time fee',
    features: ['Custom knowledge base', 'Brand-safe responses', 'Reduced call volume']
  }, {
    icon: CalendarCheckIcon,
    name: 'Appointment Booking',
    price: 'AUD $399',
    setup: 'one-time setup',
    features: ['Calendar sync', 'Automated confirmations', 'Reminder notifications']
  }];
  return <section className="w-full bg-[#1A1A1A] py-28" data-id="element-2408">
      <div ref={ref} className={`max-w-7xl mx-auto px-8 md:px-12 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`} data-id="element-2409">
        <div className="text-center mb-16" data-id="element-2410">
          <h2 className="font-['Poppins'] font-bold text-4xl md:text-5xl text-[#F5F5F5] mb-6" data-id="element-2411">
            Enhance Your Service
          </h2>
          <p className="font-['Inter'] text-lg text-[#A0A0A0] max-w-3xl mx-auto font-light" data-id="element-2412">
            Add powerful features to customize your AI answering service
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-2413">
          {addOns.map((addOn, index) => {
          const Icon = addOn.icon;
          return <div key={index} className="bg-[#252525] rounded-2xl border border-[#2A2A2A] hover:border-[#F1B530]/50 transition-all duration-300 hover:-translate-y-1 p-8" data-id="element-2414">
                <div className="w-14 h-14 bg-[#F1B530] rounded-xl flex items-center justify-center mb-6" data-id="element-2415">
                  <Icon className="w-7 h-7 text-[#0F0F0F]" data-id="element-2416" />
                </div>

                <h3 className="font-['Poppins'] font-bold text-2xl text-[#F5F5F5] mb-3" data-id="element-2417">
                  {addOn.name}
                </h3>

                <div className="mb-6" data-id="element-2418">
                  <span className="font-['Poppins'] font-semibold text-2xl text-[#F1B530]" data-id="element-2419">
                    {addOn.price}
                  </span>
                  <span className="text-[#A0A0A0] font-['Inter'] text-sm ml-2" data-id="element-2420">
                    {addOn.setup}
                  </span>
                </div>

                <ul className="space-y-3" data-id="element-2421">
                  {addOn.features.map((feature, idx) => <li key={idx} className="flex items-start gap-3" data-id="element-2422">
                      <CheckIcon className="w-5 h-5 text-[#F1B530] flex-shrink-0 mt-0.5" data-id="element-2423" />
                      <span className="font-['Inter'] text-[#F5F5F5] text-sm font-light" data-id="element-2424">
                        {feature}
                      </span>
                    </li>)}
                </ul>
              </div>;
        })}
        </div>
      </div>
    </section>;
}
function OfficeHQAdvancedAutomation() {
  const {
    ref,
    isVisible
  } = useScrollAnimation({
    threshold: 0.1
  });
  const capabilities = [{
    icon: WorkflowIcon,
    text: 'Intelligent lead routing'
  }, {
    icon: ZapIcon,
    text: 'Automated workflows'
  }, {
    icon: DatabaseIcon,
    text: 'Direct CRM sync'
  }, {
    icon: BrainCircuitIcon,
    text: 'Data collection'
  }];
  return <section className="w-full bg-[#0F0F0F] py-28" data-id="element-2425">
      <div ref={ref} className={`max-w-6xl mx-auto px-8 md:px-12 text-center transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`} data-id="element-2426">
        <h2 className="font-['Poppins'] font-bold text-4xl md:text-5xl lg:text-6xl mb-6 text-[#F5F5F5]" data-id="element-2427">
          Enterprise Automation
        </h2>
        <p className="font-['Inter'] font-light text-xl md:text-2xl text-[#A0A0A0] mb-10" data-id="element-2428">
          Advanced capabilities for high-volume operations
        </p>

        <p className="font-['Inter'] text-lg text-[#A0A0A0] max-w-4xl mx-auto mb-16 leading-relaxed font-light" data-id="element-2429">
          For businesses handling 500+ calls per month, unlock enterprise-grade
          features that transform your communication infrastructure.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12" data-id="element-2430">
          {capabilities.map((capability, index) => {
          const Icon = capability.icon;
          return <div key={index} className="bg-[#252525] rounded-2xl p-8 border border-[#2A2A2A] hover:border-[#F1B530]/30 transition-all duration-300 h-full flex flex-col items-center justify-center min-h-[180px]" data-id="element-2431">
                <div className="w-12 h-12 rounded-xl bg-[#F1B530]/10 flex items-center justify-center mb-5" data-id="element-2432">
                  <Icon className="w-6 h-6 text-[#F1B530]" data-id="element-2433" />
                </div>
                <p className="font-['Inter'] text-[#F5F5F5] font-light text-center" data-id="element-2434">
                  {capability.text}
                </p>
              </div>;
        })}
        </div>

        <Button size="lg" data-id="element-2435">Contact Sales</Button>
      </div>
    </section>;
}
function IndustryExpertise() {
  const {
    ref,
    isVisible
  } = useScrollAnimation({
    threshold: 0.1
  });
  const industries = [{
    name: 'Healthcare & Medical',
    benefits: ['HIPAA compliant call handling', 'Patient appointment scheduling', 'Emergency call prioritization', 'Secure message delivery']
  }, {
    name: 'Legal Services',
    benefits: ['Confidential client intake', 'New client screening', 'Urgent matter escalation', 'Case inquiry management']
  }, {
    name: 'Real Estate',
    benefits: ['Property inquiry handling', 'Inspection booking', 'Lead capture & qualification', 'Tenant support services']
  }, {
    name: 'Trade & Field Services',
    benefits: ['Job booking & scheduling', 'Emergency call routing', 'Quote request handling', 'Service appointment management']
  }, {
    name: 'Retail & E-commerce',
    benefits: ['Order inquiry management', 'Store information requests', 'Product availability checks', 'Customer service support']
  }, {
    name: 'Professional Services',
    benefits: ['Client screening & qualification', 'Consultation booking', 'Professional message handling', 'Appointment coordination']
  }];
  return <section className="w-full bg-[#0F0F0F] py-28" data-id="element-2436">
      <div ref={ref} className={`max-w-7xl mx-auto px-8 md:px-12 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`} data-id="element-2437">
        <div className="text-center mb-16" data-id="element-2438">
          <h2 className="font-['Poppins'] font-bold text-5xl md:text-6xl text-[#F5F5F5] mb-6" data-id="element-2439">
            Industry-Specific Solutions
          </h2>
          <p className="font-['Inter'] text-xl text-[#A0A0A0] font-light" data-id="element-2440">
            Tailored call handling for your industry's unique needs
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-2441">
          {industries.map((industry, index) => <div key={index} className="bg-[#252525] border border-[#2A2A2A] rounded-2xl p-8 hover:border-[#F1B530]/30 transition-all duration-300" data-id="element-2442">
              <h3 className="font-['Poppins'] font-bold text-2xl text-[#F5F5F5] mb-6" data-id="element-2443">
                {industry.name}
              </h3>
              <ul className="space-y-3" data-id="element-2444">
                {industry.benefits.map((benefit, idx) => <li key={idx} className="font-['Inter'] text-[#A0A0A0] flex items-start font-light" data-id="element-2445">
                    <span className="text-[#F1B530] mr-2" data-id="element-2446">•</span>
                    {benefit}
                  </li>)}
              </ul>
            </div>)}
        </div>
      </div>
    </section>;
}
function HowItWorksAnimated() {
  const {
    ref,
    isVisible
  } = useScrollAnimation({
    threshold: 0.1
  });
  const steps = [{
    number: '01',
    icon: PhoneIcon,
    title: 'Call Received',
    description: 'Your AI receptionist answers instantly with your custom greeting, creating a professional first impression.'
  }, {
    number: '02',
    icon: ClipboardCheckIcon,
    title: 'Information Captured',
    description: 'Caller details, message, and context are accurately recorded and transcribed in real-time.'
  }, {
    number: '03',
    icon: BellIcon,
    title: 'Instant Notification',
    description: 'You receive immediate alerts via SMS or email with complete call summary and next steps.'
  }, {
    number: '04',
    icon: SendIcon,
    title: 'Seamless Integration',
    description: 'Call data automatically syncs to your CRM, calendar, or workflow tools for immediate action.'
  }];
  return <section className="w-full bg-[#0F0F0F] py-28" data-id="element-2447">
      <div ref={ref} className={`max-w-7xl mx-auto px-8 md:px-12 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`} data-id="element-2448">
        <div className="text-center mb-16" data-id="element-2449">
          <h2 className="font-['Poppins'] font-bold text-5xl md:text-6xl text-[#F5F5F5] mb-6" data-id="element-2450">
            How It Works
          </h2>
          <p className="font-['Inter'] text-xl text-[#A0A0A0] font-light max-w-3xl mx-auto" data-id="element-2451">
            From call to action in four simple steps
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8" data-id="element-2452">
          {steps.map((step, index) => {
          const Icon = step.icon;
          return <div key={index} className="relative" data-id="element-2453">
                {/* Connector line */}
                {index < steps.length - 1 && <div className="hidden lg:block absolute top-16 left-[60%] w-[80%] h-px bg-gradient-to-r from-[#F1B530] to-transparent" data-id="element-2454"></div>}

                <div className="bg-[#252525] border border-[#2A2A2A] rounded-2xl p-8 hover:border-[#F1B530]/30 transition-all duration-300 h-full" data-id="element-2455">
                  <div className="font-['Poppins'] text-5xl font-bold text-[#F1B530]/20 mb-4" data-id="element-2456">
                    {step.number}
                  </div>

                  <div className="w-14 h-14 bg-[#F1B530]/10 rounded-xl flex items-center justify-center mb-6 border border-[#F1B530]/20" data-id="element-2457">
                    <Icon className="w-7 h-7 text-[#F1B530]" data-id="element-2458" />
                  </div>

                  <h3 className="font-['Poppins'] font-bold text-xl text-[#F5F5F5] mb-4" data-id="element-2459">
                    {step.title}
                  </h3>

                  <p className="font-['Inter'] text-[#A0A0A0] leading-relaxed font-light text-sm" data-id="element-2460">
                    {step.description}
                  </p>
                </div>
              </div>;
        })}
        </div>
      </div>
    </section>;
}
function IndustrySpecificSolutionsAnimated() {
  const {
    ref,
    isVisible
  } = useScrollAnimation({
    threshold: 0.1
  });
  const industries = [{
    name: 'Legal Services',
    description: 'HIPAA-compliant call handling for law firms',
    features: ['Client intake screening', 'Confidential message handling', 'Urgent matter escalation', 'Case management integration']
  }, {
    name: 'Healthcare',
    description: 'Secure patient communication solutions',
    features: ['Appointment scheduling', 'Patient information capture', 'Emergency call routing', 'HIPAA compliance']
  }, {
    name: 'Real Estate',
    description: 'Never miss a property inquiry',
    features: ['Property inquiry handling', 'Viewing appointment booking', 'Lead qualification', 'Tenant support']
  }, {
    name: 'Professional Services',
    description: 'Maintain professional image 24/7',
    features: ['Client screening', 'Consultation booking', 'Message prioritization', 'Multi-office support']
  }, {
    name: 'Home Services',
    description: 'Capture every service request',
    features: ['Job booking', 'Emergency call handling', 'Quote request capture', 'Service scheduling']
  }, {
    name: 'Financial Services',
    description: 'Secure and compliant communication',
    features: ['Client verification', 'Appointment scheduling', 'Compliance adherence', 'Secure messaging']
  }];
  return <section className="w-full bg-[#1A1A1A] py-28" data-id="element-2461">
      <div ref={ref} className={`max-w-7xl mx-auto px-8 md:px-12 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`} data-id="element-2462">
        <div className="text-center mb-16" data-id="element-2463">
          <h2 className="font-['Poppins'] font-bold text-5xl md:text-6xl text-[#F5F5F5] mb-6" data-id="element-2464">
            Industry-Specific Solutions
          </h2>
          <p className="font-['Inter'] text-xl text-[#A0A0A0] font-light max-w-3xl mx-auto" data-id="element-2465">
            Tailored AI answering services for your industry's unique needs
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-2466">
          {industries.map((industry, index) => <div key={index} className="bg-[#252525] border border-[#2A2A2A] rounded-2xl p-8 hover:border-[#F1B530]/30 transition-all duration-300" data-id="element-2467">
              <h3 className="font-['Poppins'] font-bold text-2xl text-[#F5F5F5] mb-3" data-id="element-2468">
                {industry.name}
              </h3>
              <p className="font-['Inter'] text-[#A0A0A0] mb-6 text-sm font-light" data-id="element-2469">
                {industry.description}
              </p>
              <ul className="space-y-3" data-id="element-2470">
                {industry.features.map((feature, idx) => <li key={idx} className="flex items-start gap-3" data-id="element-2471">
                    <CheckIcon className="w-5 h-5 text-[#F1B530] flex-shrink-0 mt-0.5" data-id="element-2472" />
                    <span className="font-['Inter'] text-[#F5F5F5] text-sm font-light" data-id="element-2473">
                      {feature}
                    </span>
                  </li>)}
              </ul>
            </div>)}
        </div>
      </div>
    </section>;
}
function OfficeHQFAQAnimated() {
  const {
    ref,
    isVisible
  } = useScrollAnimation({
    threshold: 0.1
  });
  const [openIndex, setOpenIndex] = useState<number | null>(0);
  const faqs = [{
    question: 'How does the AI answering service work?',
    answer: 'Our AI receptionist answers calls 24/7 using your custom greeting and protocols. It captures caller information, handles FAQs, and can book appointments or transfer urgent calls. All interactions are recorded and you receive instant notifications with complete call summaries.'
  }, {
    question: 'Can I customize the greeting and responses?',
    answer: 'Absolutely. You have full control over your company greeting, call handling protocols, and FAQ responses. Our system learns your business terminology and can be trained to handle industry-specific questions while maintaining your brand voice.'
  }, {
    question: "What happens if the AI can't handle a call?",
    answer: "The AI can seamlessly transfer calls to your team with full context when needed. You define the escalation rules - whether it's for urgent matters, specific request types, or VIP callers. The transfer includes all captured information so your team has complete context."
  }, {
    question: 'How quickly can I get started?',
    answer: 'Most businesses are live within 24 hours. Setup involves configuring your greeting, setting up call routing rules, and integrating with your existing tools. Our team provides hands-on support throughout the process to ensure everything works perfectly.'
  }, {
    question: 'Is my data secure and compliant?',
    answer: 'Yes. We maintain SOC 2 Type II certification and are HIPAA compliant for healthcare clients. All call data is encrypted in transit and at rest. We never share your data with third parties and provide complete audit trails for compliance requirements.'
  }, {
    question: 'What integrations are available?',
    answer: 'We integrate with major CRMs (Salesforce, HubSpot, Zoho), calendar systems (Google Calendar, Outlook, Calendly), communication tools (Slack, Teams), and can connect to custom systems via API. Our team can help set up any integration you need.'
  }];
  return <section className="w-full bg-[#0F0F0F] py-28" data-id="element-2474">
      <div ref={ref} className={`max-w-4xl mx-auto px-8 md:px-12 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`} data-id="element-2475">
        <div className="text-center mb-16" data-id="element-2476">
          <h2 className="font-['Poppins'] font-bold text-4xl md:text-5xl text-[#F5F5F5] mb-6" data-id="element-2477">
            Frequently Asked Questions
          </h2>
          <p className="font-['Inter'] text-lg text-[#A0A0A0] font-light" data-id="element-2478">
            Everything you need to know about our AI answering service
          </p>
        </div>

        <div className="space-y-4" data-id="element-2479">
          {faqs.map((faq, index) => <div key={index} className="bg-[#252525] border border-[#2A2A2A] rounded-2xl overflow-hidden transition-all duration-300 hover:border-[#F1B530]/30" data-id="element-2480">
              <button onClick={() => setOpenIndex(openIndex === index ? null : index)} className="w-full px-8 py-6 flex items-center justify-between text-left" data-id="element-2481">
                <span className="font-['Inter'] font-semibold text-xl text-[#F5F5F5] pr-8" data-id="element-2482">
                  {faq.question}
                </span>
                <ChevronDownIcon className={`w-6 h-6 text-[#F1B530] flex-shrink-0 transition-transform duration-300 ${openIndex === index ? 'rotate-180' : ''}`} data-id="element-2483" />
              </button>

              <div className={`overflow-hidden transition-all duration-300 ${openIndex === index ? 'max-h-96' : 'max-h-0'}`} data-id="element-2484">
                <div className="px-8 pb-6" data-id="element-2485">
                  <p className="font-['Inter'] text-[#A0A0A0] leading-relaxed font-light" data-id="element-2486">
                    {faq.answer}
                  </p>
                </div>
              </div>
            </div>)}
        </div>

        <div className="text-center mt-12" data-id="element-2487">
          <p className="font-['Inter'] text-[#A0A0A0] mb-6" data-id="element-2488">
            Still have questions? We're here to help.
          </p>
          <Button variant="secondary" theme="dark" data-id="element-2489">
            Contact Support
          </Button>
        </div>
      </div>
    </section>;
}
export function OfficeHQInspired() {
  return <div className="w-full min-h-screen bg-[#0F0F0F]" data-id="element-2490">
      <ProfessionalHero data-id="element-2491" />
      <TrustBadgesAnimated data-id="element-2492" />
      <HowItWorksAnimated data-id="element-2493" />
      <ServiceBreakdown data-id="element-2494" />
      <IndustrySpecificSolutionsAnimated data-id="element-2495" />
      <ComparisonTable data-id="element-2496" />
      <OfficeHQPricing data-id="element-2497" />
      <OfficeHQTestimonials data-id="element-2498" />
      <OfficeHQAddOns data-id="element-2499" />
      <OfficeHQAdvancedAutomation data-id="element-2500" />
      <OfficeHQFAQAnimated data-id="element-2501" />
    </div>;
}